import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { languagesApi, statsApi } from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ChevronLeft, ChevronRight, CheckCircle, RotateCcw, BarChart3, Cloud, CloudOff } from 'lucide-react';

// ─── Assessment spec (from forge_language_priority_assessment.json) ────────────

const QUESTIONS = [
  { id: 1, category: 'Goals', prompt: 'How important is your next major goal in this language?', explanation: 'Consider how much achieving your next meaningful goal would matter in your life, studies, work, relationships, or personal development. Rate the importance of the goal itself, not how soon it must happen.' },
  { id: 2, category: 'Goals', prompt: 'How soon do you need to reach your target level?', explanation: 'Think about whether you have a real deadline, such as a trip, exam, work requirement, planned conversation, or project. A high rating means the target is needed soon; no foreseeable deadline should receive a low rating.' },
  { id: 3, category: 'Goals', prompt: 'How useful would this language be for your future?', explanation: 'Consider likely benefits over the coming years, including career opportunities, education, travel, relationships, access to information, or meaningful personal goals.' },
  { id: 4, category: 'Goals', prompt: 'How motivated are you to learn this language for its own sake?', explanation: 'Consider how willing you are to return to this language even when nobody requires it and no immediate reward is available. Focus on your own desire to learn.' },
  { id: 5, category: 'Goals', prompt: 'How strong is your personal affinity for this language and its culture?', explanation: 'Think about how naturally you are drawn to the language, its sounds, media, history, literature, people, or cultural context. Rate your lasting interest and connection.' },
  { id: 6, category: 'Goals', prompt: 'How important is it to maintain momentum toward an upcoming milestone?', explanation: 'Consider whether you need consistent progress to be ready for a specific event, project, exam, trip, or conversation.' },
  { id: 7, category: 'Proficiency', prompt: 'How far are you from the proficiency level you want to reach?', explanation: 'Compare your current ability with the level you personally want or need. A high rating means there is a large gap to close; a low rating means you are already close to your target.' },
  { id: 8, category: 'Proficiency', prompt: 'How much are you struggling or making little progress in this language?', explanation: 'Consider recurring difficulties, persistent errors, comprehension problems, or a feeling that your ability has stopped improving despite effort.' },
  { id: 9, category: 'Proficiency', prompt: 'How much are you at risk of forgetting what you have learned?', explanation: 'Consider whether vocabulary, grammar, comprehension, or speaking ability is fading because you rarely revisit or use the language.' },
  { id: 10, category: 'Proficiency', prompt: 'How serious are your weaknesses in the essential skills for this language?', explanation: 'Think about the skills that matter for your goals, such as listening, speaking, reading, writing, vocabulary, pronunciation, or grammar.' },
  { id: 11, category: 'Learning', prompt: 'How effectively does your study time turn into measurable progress?', explanation: 'Consider what you actually retain or become able to do after studying, relative to the time and effort you invest.' },
  { id: 12, category: 'Learning', prompt: 'How much useful learning material do you already have for this language?', explanation: 'Consider materials you can realistically study now, such as saved texts, vocabulary, flashcards, audio, lessons, or graded reading.' },
  { id: 13, category: 'Learning', prompt: 'How many realistic opportunities do you have to use this language?', explanation: 'Think about people you can speak with, communities, work, travel, media, reading, and other ways to use the language in real situations.' },
  { id: 14, category: 'Learning', prompt: 'How long has this language been neglected compared with the attention it needs?', explanation: 'Consider whether you have left the language untouched or under-practiced for longer than is sensible for your goals and current ability.' },
  { id: 15, category: 'Strategy', prompt: 'How strategically valuable is this language within your broader learning plan?', explanation: 'Consider whether it supports another language, unlocks important resources, advances a long-term project, or serves a role that other languages cannot easily fill.' },
];

const WEIGHTS = { 1:9, 2:9, 3:8, 4:7, 5:5, 6:7, 7:9, 8:6, 9:6, 10:7, 11:7, 12:5, 13:5, 14:5, 15:5 };

const PRIORITY_BANDS = [
  { id: 'critical',    min: 80, label: 'Critical',     timeMin: 30, timeMax: 40, color: 'text-red-400',    bg: 'bg-red-500/15 border-red-500/30' },
  { id: 'high',        min: 65, label: 'High',          timeMin: 20, timeMax: 30, color: 'text-orange-400', bg: 'bg-orange-500/15 border-orange-500/30' },
  { id: 'medium',      min: 45, label: 'Medium',        timeMin: 10, timeMax: 20, color: 'text-amber-400',  bg: 'bg-amber-500/15 border-amber-500/30' },
  { id: 'low',         min: 25, label: 'Low',           timeMin: 5,  timeMax: 10, color: 'text-blue-400',   bg: 'bg-blue-500/15 border-blue-500/30' },
  { id: 'maintenance', min: 0,  label: 'Maintenance',   timeMin: 2,  timeMax: 5,  color: 'text-slate-400',  bg: 'bg-slate-500/15 border-slate-500/30' },
];

const STORAGE_KEY = 'iling_priority_assessment_v1';

// ─── Storage helpers (localStorage cache) ────────────────────────────────────

function loadStorage() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {}; } catch { return {}; }
}
const loadLocal = loadStorage;

function saveLocal(data) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

// ─── Scoring (internal, never shown to user) ──────────────────────────────────

function computeScores(answers) {
  const base = Object.entries(WEIGHTS).reduce((sum, [qid, w]) => {
    return sum + (answers[parseInt(qid)] ?? 0) * w;
  }, 0) / 10;

  const need = 10 * (
    0.30 * (answers[7] ?? 0) +
    0.20 * (answers[8] ?? 0) +
    0.20 * (answers[9] ?? 0) +
    0.20 * (answers[10] ?? 0) +
    0.10 * (answers[14] ?? 0)
  );

  const final = 0.80 * base + 0.20 * need;
  return final;
}

function getPriorityBand(score) {
  for (const band of PRIORITY_BANDS) {
    if (score >= band.min) return band;
  }
  return PRIORITY_BANDS[PRIORITY_BANDS.length - 1];
}

function getMainReasons(answers) {
  const reasons = [];
  if ((answers[2] ?? 0) >= 7) reasons.push('Upcoming deadline or goal');
  if ((answers[7] ?? 0) >= 7) reasons.push('Large proficiency gap to close');
  if ((answers[9] ?? 0) >= 7) reasons.push('High risk of forgetting');
  if ((answers[1] ?? 0) >= 7) reasons.push('Very important personal goal');
  if ((answers[4] ?? 0) >= 7) reasons.push('Strong intrinsic motivation');
  if ((answers[14] ?? 0) >= 7) reasons.push('Language has been neglected');
  if ((answers[15] ?? 0) >= 7) reasons.push('High strategic value');
  return reasons.slice(0, 3);
}

// ─── Single question card ─────────────────────────────────────────────────────

function QuestionCard({ question, value, onChange, onPrev, onNext, onFinish, currentIndex, total }) {
  const isLast = currentIndex === total - 1;
  const answered = value !== null && value !== undefined;

  return (
    <div className="space-y-5">
      {/* Progress */}
      <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
        <span className="font-medium text-primary/70 uppercase tracking-wide text-[10px]">{question.category}</span>
        <span>{currentIndex + 1} / {total}</span>
      </div>
      <div className="w-full h-1 rounded-full bg-muted overflow-hidden">
        <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${((currentIndex + 1) / total) * 100}%` }} />
      </div>

      {/* Question */}
      <div className="space-y-2">
        <p className="text-base font-semibold leading-snug">{question.prompt}</p>
        <p className="text-sm text-muted-foreground leading-relaxed">{question.explanation}</p>
      </div>

      {/* Scale */}
      <div className="space-y-3">
        <div className="flex justify-between text-[10px] text-muted-foreground">
          <span>Not at all</span>
          <span>Moderately</span>
          <span>Extremely</span>
        </div>
        {/* Rating buttons 0–10 */}
        <div className="grid grid-cols-11 gap-1">
          {Array.from({ length: 11 }, (_, i) => (
            <button
              key={i}
              onClick={() => onChange(i)}
              data-testid={`rating-btn-${i}`}
              className={`h-9 rounded text-xs font-semibold transition-all border ${
                value === i
                  ? 'bg-primary text-primary-foreground border-primary scale-105 shadow'
                  : 'border-border hover:border-primary/60 hover:bg-primary/10 text-muted-foreground'
              }`}
            >
              {i}
            </button>
          ))}
        </div>
        <p className="text-[10px] text-muted-foreground text-center italic">
          Answer independently — no need to match earlier answers.
        </p>
      </div>

      {/* Navigation */}
      <div className="flex gap-2 pt-1">
        {currentIndex > 0 && (
          <Button variant="outline" size="sm" onClick={onPrev} className="gap-1">
            <ChevronLeft className="h-4 w-4" /> Back
          </Button>
        )}
        {!isLast ? (
          <Button
            size="sm"
            disabled={!answered}
            onClick={onNext}
            className="ml-auto gap-1"
            data-testid="question-next-btn"
          >
            Next <ChevronRight className="h-4 w-4" />
          </Button>
        ) : (
          <Button
            size="sm"
            disabled={!answered}
            onClick={onFinish}
            className="ml-auto gap-1 bg-green-600 hover:bg-green-700"
            data-testid="question-finish-btn"
          >
            <CheckCircle className="h-4 w-4" /> Save Assessment
          </Button>
        )}
      </div>
    </div>
  );
}

// ─── Results row ──────────────────────────────────────────────────────────────

function ResultRow({ rank, langName, answers, onRetake }) {
  const score = computeScores(answers);
  const band = getPriorityBand(score);
  const reasons = getMainReasons(answers);

  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-3 p-4 rounded-xl border bg-card">
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <span className="text-2xl font-bold text-muted-foreground/50 w-6 shrink-0 text-center">#{rank}</span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold">{langName}</span>
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold border ${band.bg} ${band.color}`}>
              {band.label}
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            Suggested time share: <strong>{band.timeMin}–{band.timeMax}%</strong>
          </p>
          {reasons.length > 0 && (
            <p className="text-xs text-muted-foreground mt-1">
              {reasons.join(' · ')}
            </p>
          )}
        </div>
      </div>
      <Button variant="ghost" size="sm" onClick={onRetake} className="gap-1.5 text-xs shrink-0">
        <RotateCcw className="h-3.5 w-3.5" /> Retake
      </Button>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function LanguagePriorityAssessment() {
  const navigate = useNavigate();
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [storage, setStorage] = useState(loadStorage);
  const [syncStatus, setSyncStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'offline'
  const saveTimer = useRef(null);
  // Per-tab state: currentQuestion index while answering
  const [tabProgress, setTabProgress] = useState({});  // { langId: questionIndex }

  // Load from API on mount (merges with localStorage cache)
  useEffect(() => {
    Promise.allSettled([
      languagesApi.getUserLanguages?.() || Promise.resolve({ data: [] }),
      statsApi.getPriorityAssessment(),
    ]).then(([langsResult, assessResult]) => {
      if (langsResult.status === 'fulfilled') {
        // API returns [{ language_id, language: { name, native_name, code }, ... }]
        // Normalise to flat { language_id, name, native_name, code }
        const langs = (langsResult.value?.data || []).filter(ul => !ul.hidden).map(ul => ({
          language_id: ul.language_id,
          name: ul.language?.name || ul.language_id,
          native_name: ul.language?.native_name || '',
          code: ul.language?.code || '',
        }));
        setLanguages(langs);
      }
      if (assessResult.status === 'fulfilled') {
        const r = assessResult.value;
        if (r.data?.assessments && Object.keys(r.data.assessments).length > 0) {
          setStorage(r.data.assessments);
          saveLocal(r.data.assessments);
        }
      } else {
        setSyncStatus('offline');
      }
    }).finally(() => setLoading(false));
  }, []);

  // Debounced save to API + localStorage on every storage change
  useEffect(() => {
    saveLocal(storage);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setSyncStatus('saving');
    saveTimer.current = setTimeout(() => {
      statsApi.savePriorityAssessment(storage)
        .then(() => setSyncStatus('saved'))
        .catch(() => setSyncStatus('offline'));
    }, 1200);
    return () => clearTimeout(saveTimer.current);
  }, [storage]);

  const getLangData = (langId) => storage[langId] || { status: 'not_started', answers: {} };

  const getQuestionIndex = (langId) => tabProgress[langId] ?? 0;

  const handleAnswer = (langId, qid, rating) => {
    setStorage(prev => {
      const lang = prev[langId] || { status: 'in_progress', answers: {} };
      return {
        ...prev,
        [langId]: {
          ...lang,
          status: 'in_progress',
          answers: { ...lang.answers, [qid]: rating },
        },
      };
    });
  };

  const handleNext = (langId) => {
    setTabProgress(p => ({ ...p, [langId]: (p[langId] ?? 0) + 1 }));
  };

  const handlePrev = (langId) => {
    setTabProgress(p => ({ ...p, [langId]: Math.max(0, (p[langId] ?? 0) - 1) }));
  };

  const handleFinish = (langId) => {
    setStorage(prev => ({
      ...prev,
      [langId]: {
        ...prev[langId],
        status: 'completed',
        completed_at: new Date().toISOString(),
      },
    }));
  };

  const handleRetake = (langId) => {
    setStorage(prev => ({
      ...prev,
      [langId]: { status: 'in_progress', answers: {} },
    }));
    setTabProgress(p => ({ ...p, [langId]: 0 }));
  };

  // Ranked results for completed languages
  const completedLangs = languages.filter(l => getLangData(l.language_id).status === 'completed');
  const rankedLangs = [...completedLangs].sort((a, b) =>
    computeScores(getLangData(b.language_id).answers) - computeScores(getLangData(a.language_id).answers)
  );

  if (loading) return (
    <div className="p-6 text-muted-foreground text-sm">Loading languages…</div>
  );

  if (languages.length === 0) return (
    <div className="p-6">
      <p className="text-muted-foreground text-sm">No languages found. Add languages from the Dashboard first.</p>
      <Button className="mt-4" onClick={() => navigate('/dashboard')}>Go to Dashboard</Button>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 space-y-8" data-testid="language-priority-page">
      {/* Header */}
      <div className="space-y-1">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="gap-1.5 -ml-2">
            <ChevronLeft className="h-4 w-4" /> Back
          </Button>
        </div>
        <h1 className="text-2xl font-bold">Language Priority Assessment</h1>
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Rate each language independently across 15 questions. Results guide your Study Plan allocation.
          </p>
          {syncStatus === 'saving' && <span className="text-[11px] text-muted-foreground shrink-0 ml-2">Saving…</span>}
          {syncStatus === 'saved' && <span className="text-[11px] text-green-500 shrink-0 ml-2 flex items-center gap-1"><Cloud className="h-3 w-3" /> Saved</span>}
          {syncStatus === 'offline' && <span className="text-[11px] text-amber-400 shrink-0 ml-2 flex items-center gap-1"><CloudOff className="h-3 w-3" /> Local only</span>}
        </div>
      </div>

      {/* Results panel (only when at least one language completed) */}
      {rankedLangs.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-primary" />
            <h2 className="font-semibold">Priority Ranking</h2>
            <span className="text-xs text-muted-foreground ml-auto">
              {completedLangs.length}/{languages.length} assessed
            </span>
          </div>
          <div className="space-y-2">
            {rankedLangs.map((lang, i) => (
              <ResultRow
                key={lang.language_id}
                rank={i + 1}
                langName={lang.name}
                answers={getLangData(lang.language_id).answers}
                onRetake={() => handleRetake(lang.language_id)}
              />
            ))}
          </div>
          {completedLangs.length < languages.length && (
            <p className="text-xs text-muted-foreground px-1">
              Complete the remaining assessments below to include those languages in the ranking.
            </p>
          )}
        </div>
      )}

      {/* Per-language assessment tabs */}
      <div className="space-y-3">
        <h2 className="font-semibold">Assessments</h2>
        <Tabs defaultValue={languages[0]?.language_id} className="w-full">
          <TabsList className="flex flex-wrap h-auto gap-1 bg-muted/50 p-1 mb-4">
            {languages.map(lang => {
              const s = getLangData(lang.language_id).status;
              return (
                <TabsTrigger
                  key={lang.language_id}
                  value={lang.language_id}
                  className="text-xs gap-1"
                  data-testid={`lang-tab-${lang.language_id}`}
                >
                  {lang.name}
                  {s === 'completed' && <span className="text-green-500 ml-1">✓</span>}
                  {s === 'in_progress' && <span className="text-amber-400 ml-1">•</span>}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {languages.map(lang => {
            const langData = getLangData(lang.language_id);
            const qIdx = getQuestionIndex(lang.language_id);
            const question = QUESTIONS[qIdx];
            const answers = langData.answers;

            return (
              <TabsContent key={lang.language_id} value={lang.language_id}>
                <Card>
                  <CardContent className="pt-6">
                    {langData.status === 'not_started' && (
                      <div className="text-center py-6 space-y-3">
                        <p className="text-muted-foreground text-sm">
                          Answer 15 questions to assess <strong>{lang.name}</strong>'s priority in your study plan.
                        </p>
                        <Button
                          onClick={() => {
                            setStorage(prev => ({
                              ...prev,
                              [lang.language_id]: { status: 'in_progress', answers: {} },
                            }));
                            setTabProgress(p => ({ ...p, [lang.language_id]: 0 }));
                          }}
                          data-testid={`start-assessment-${lang.language_id}`}
                          className="gap-2"
                        >
                          Start Assessment
                        </Button>
                      </div>
                    )}

                    {langData.status === 'in_progress' && question && (
                      <QuestionCard
                        question={question}
                        value={answers[question.id] ?? null}
                        onChange={(rating) => handleAnswer(lang.language_id, question.id, rating)}
                        onPrev={() => handlePrev(lang.language_id)}
                        onNext={() => handleNext(lang.language_id)}
                        onFinish={() => handleFinish(lang.language_id)}
                        currentIndex={qIdx}
                        total={QUESTIONS.length}
                      />
                    )}

                    {langData.status === 'completed' && (
                      <div className="text-center py-6 space-y-3">
                        <CheckCircle className="h-10 w-10 text-green-500 mx-auto" />
                        <p className="font-semibold">Assessment complete for {lang.name}</p>
                        <p className="text-xs text-muted-foreground">
                          See the Priority Ranking above for results.
                        </p>
                        <Button variant="outline" size="sm" onClick={() => handleRetake(lang.language_id)} className="gap-2">
                          <RotateCcw className="h-4 w-4" /> Retake Assessment
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            );
          })}
        </Tabs>
      </div>
    </div>
  );
}
